import { useState, useEffect, useMemo, useRef } from 'react'
import { usePlayerStore } from '../stores'
import { ClubStaff } from '../types'
import {
  Users,
  Plus,
  Search,
  Loader2,
  AlertCircle,
  Edit,
  Trash2,
  Eye,
  ArrowLeft,
  Download,
  Upload,
  Printer,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Shield,
  Briefcase,
  X,
  CheckCircle2,
  Clock,
  XCircle,
  Activity,
} from 'lucide-react'
import ImageUpload from './ImageUpload'
import { read, utils } from 'xlsx'

type StatusFilter = 'all' | 'active' | 'on_leave' | 'contract' | 'terminated'

interface ClubStaffManagementProps {
  clubId: string
  clubName: string
}

const STATUS_CONFIG = {
  active: { label: 'Active', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  on_leave: { label: 'On Leave', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  contract: { label: 'Contract', icon: Briefcase, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  terminated: { label: 'Terminated', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
}

const POSITION_OPTIONS = [
  'Head Coach',
  'Assistant Coach',
  'Goalkeeping Coach',
  'Fitness Coach',
  'Physio',
  'Team Manager',
  'Scout',
  'Analyst',
  'Kit Manager',
  'Director',
  'Other',
]

const CONTRACT_OPTIONS = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'volunteer', label: 'Volunteer' },
]

function ClubStaffManagement({ clubId, clubName }: ClubStaffManagementProps) {
  const { staff, staffLoading, fetchStaff, addStaff, updateStaff, deleteStaff } = usePlayerStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selectedStaff, setSelectedStaff] = useState<ClubStaff | null>(null)
  const [editingStaff, setEditingStaff] = useState<ClubStaff | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showReport, setShowReport] = useState<ClubStaff | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResults, setImportResults] = useState<{ success: number; errors: string[] } | null>(null)
  const reportRef = useRef<HTMLDivElement>(null)
  const importFileRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    full_name: '',
    photo_url: '',
    email: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    position: '',
    employment_status: 'active' as ClubStaff['employment_status'],
    contract_type: '' as '' | ClubStaff['contract_type'],
    start_date: '',
    end_date: '',
    notes: '',
  })

  useEffect(() => {
    if (clubId) fetchStaff(clubId)
  }, [clubId, fetchStaff])

  const filteredStaff = useMemo(() => {
    let result = staff
    if (statusFilter !== 'all') {
      result = result.filter((s) => s.employment_status === statusFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (s) =>
          s.full_name.toLowerCase().includes(q) ||
          s.position.toLowerCase().includes(q) ||
          (s.email && s.email.toLowerCase().includes(q))
      )
    }
    return result
  }, [staff, statusFilter, searchQuery])

  const statusCounts = useMemo(() => {
    const counts = { active: 0, on_leave: 0, contract: 0, terminated: 0 }
    staff.forEach((s) => {
      if (counts[s.employment_status as keyof typeof counts] !== undefined) {
        counts[s.employment_status as keyof typeof counts]++
      }
    })
    return counts
  }, [staff])

  const resetForm = () => {
    setFormData({
      full_name: '',
      photo_url: '',
      email: '',
      phone: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      position: '',
      employment_status: 'active',
      contract_type: '',
      start_date: '',
      end_date: '',
      notes: '',
    })
  }

  const openAddForm = () => {
    resetForm()
    setEditingStaff(null)
    setShowAddForm(true)
    setSelectedStaff(null)
  }

  const openEditForm = (member: ClubStaff) => {
    setFormData({
      full_name: member.full_name,
      photo_url: member.photo_url || '',
      email: member.email || '',
      phone: member.phone || '',
      emergency_contact_name: member.emergency_contact_name || '',
      emergency_contact_phone: member.emergency_contact_phone || '',
      position: member.position,
      employment_status: member.employment_status,
      contract_type: member.contract_type || '',
      start_date: member.start_date || '',
      end_date: member.end_date || '',
      notes: member.notes || '',
    })
    setEditingStaff(member)
    setShowAddForm(true)
    setSelectedStaff(null)
  }

  const closeForm = () => {
    setShowAddForm(false)
    setEditingStaff(null)
    resetForm()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.full_name.trim()) {
      setError('Full name is required')
      return
    }
    if (!formData.position.trim()) {
      setError('Position is required')
      return
    }

    const payload = {
      club_id: clubId,
      full_name: formData.full_name.trim(),
      photo_url: formData.photo_url || undefined,
      email: formData.email.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      emergency_contact_name: formData.emergency_contact_name.trim() || undefined,
      emergency_contact_phone: formData.emergency_contact_phone.trim() || undefined,
      position: formData.position.trim(),
      employment_status: formData.employment_status,
      contract_type: (formData.contract_type || undefined) as ClubStaff['contract_type'] | undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
      notes: formData.notes.trim() || undefined,
    }

    try {
      if (editingStaff) {
        await updateStaff(editingStaff.id, payload)
      } else {
        await addStaff(payload)
      }
      closeForm()
    } catch (err: any) {
      setError(err.message || 'Failed to save staff member')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to remove this staff member?')) return
    setProcessingId(id)
    try {
      await deleteStaff(id)
      if (selectedStaff?.id === id) setSelectedStaff(null)
    } catch (err: any) {
      setError(err.message || 'Failed to delete staff member')
    } finally {
      setProcessingId(null)
    }
  }

  const handleExportCSV = () => {
    const headers = ['Name', 'Position', 'Status', 'Contract Type', 'Email', 'Phone', 'Start Date', 'End Date', 'Emergency Contact', 'Emergency Phone', 'Notes']
    const rows = filteredStaff.map((s) => [
      s.full_name,
      s.position,
      STATUS_CONFIG[s.employment_status]?.label || s.employment_status,
      s.contract_type || '',
      s.email || '',
      s.phone || '',
      s.start_date || '',
      s.end_date || '',
      s.emergency_contact_name || '',
      s.emergency_contact_phone || '',
      s.notes || '',
    ])
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${clubName.replace(/\s+/g, '_')}_staff_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handlePrintReport = () => {
    window.print()
  }

  const downloadStaffTemplate = () => {
    const csv = 'full_name,position,employment_status,contract_type,email,phone,emergency_contact_name,emergency_contact_phone,start_date,end_date,notes\n'
    const samples = [
      'John Doe,Head Coach,active,full_time,john@club.com,+2348012345678,Jane Doe,+23480987654321,2025-01-15,,Experienced UEFA B licensed coach',
      'Jane Smith,Physio,active,part_time,jane@club.com,+2348023456789,John Smith,+2348011223344,2025-03-01,2026-03-01,Sports physiotherapy specialist',
      'Mike Johnson,Assistant Coach,on_leave,contract,mike@club.com,,,2024-06-15,,Currently on medical leave',
    ]
    const blob = new Blob([csv + samples.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const element = document.createElement('a')
    element.href = URL.createObjectURL(blob)
    element.download = 'staff_import_template.csv'
    element.click()
    URL.revokeObjectURL(element.href)
  }

  const handleImportFile = async (file: File) => {
    const name = file.name.toLowerCase()
    const isCsv = name.endsWith('.csv')
    const isExcel = name.endsWith('.xls') || name.endsWith('.xlsx')
    if (!isCsv && !isExcel) {
      alert('Please upload a .csv, .xls, or .xlsx file')
      return
    }

    setImporting(true)
    setImportResults(null)

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        let text = ''
        if (isExcel) {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = read(data, { type: 'array' })
          const firstSheet = workbook.SheetNames[0]
          text = utils.sheet_to_csv(workbook.Sheets[firstSheet])
        } else {
          const decoder = new TextDecoder('utf-8')
          text = decoder.decode(e.target?.result as ArrayBuffer)
        }

        const lines = text.split('\n').filter((line) => line.trim())
        if (lines.length < 2) {
          setImportResults({ success: 0, errors: ['File appears to be empty or has no data rows.'] })
          return
        }

        const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'))
        const errors: string[] = []
        let successCount = 0

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map((v) => v.trim().replace(/^"|"$/g, ''))
          if (values.length < headers.length) continue

          const row: Record<string, string> = {}
          headers.forEach((header, index) => {
            row[header] = values[index] || ''
          })

          if (!row.full_name && !row.name) {
            errors.push(`Row ${i + 1}: Missing full_name`)
            continue
          }
          if (!row.position) {
            errors.push(`Row ${i + 1}: Missing position`)
            continue
          }

          const validStatus = ['active', 'on_leave', 'terminated', 'contract']
          const status = validStatus.includes(row.employment_status?.toLowerCase()) ? row.employment_status.toLowerCase() : 'active'
          const validContracts = ['full_time', 'part_time', 'contract', 'volunteer']
          const contractType = validContracts.includes(row.contract_type?.toLowerCase()) ? row.contract_type.toLowerCase() : undefined

          try {
            await addStaff({
              club_id: clubId,
              full_name: row.full_name || row.name,
              position: row.position,
              employment_status: status as ClubStaff['employment_status'],
              contract_type: contractType as ClubStaff['contract_type'] | undefined,
              email: row.email || undefined,
              phone: row.phone || undefined,
              emergency_contact_name: row.emergency_contact_name || undefined,
              emergency_contact_phone: row.emergency_contact_phone || undefined,
              start_date: row.start_date || undefined,
              end_date: row.end_date || undefined,
              notes: row.notes || undefined,
            })
            successCount++
          } catch (err) {
            errors.push(`Row ${i + 1}: Failed to add staff member`)
          }
        }

        setImportResults({ success: successCount, errors })
      } catch (err) {
        setImportResults({ success: 0, errors: ['Failed to parse file. Ensure it follows the template format.'] })
      } finally {
        setImporting(false)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  // ===== DETAIL VIEW =====
  if (selectedStaff) {
    const statusInfo = STATUS_CONFIG[selectedStaff.employment_status]
    const StatusIcon = statusInfo?.icon || Activity

    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedStaff(null)}
            className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Back to Staff
          </button>
        </div>

        <div className="rounded-2xl border border-gray-800 bg-[#0d1117] overflow-hidden">
          <div className="p-6 border-b border-gray-800 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {selectedStaff.photo_url ? (
              <img
                src={selectedStaff.photo_url}
                alt={selectedStaff.full_name}
                className="w-20 h-20 rounded-2xl object-cover border border-gray-700"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gray-800 flex items-center justify-center border border-gray-700">
                <User size={32} className="text-gray-500" />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-display font-black text-white">{selectedStaff.full_name}</h2>
              <p className="text-sm text-gray-400 mt-1">{selectedStaff.position}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${statusInfo?.bg} ${statusInfo?.color} ${statusInfo?.border}`}>
                  <StatusIcon size={12} />
                  {statusInfo?.label}
                </span>
                {selectedStaff.contract_type && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gray-800 text-gray-400 border border-gray-700">
                    {CONTRACT_OPTIONS.find((c) => c.value === selectedStaff.contract_type)?.label || selectedStaff.contract_type}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowReport(selectedStaff)}
                className="px-4 py-2 rounded-xl bg-gray-800 text-gray-300 hover:text-white text-xs font-bold transition flex items-center gap-2"
              >
                <Printer size={14} /> Report
              </button>
              <button
                onClick={() => openEditForm(selectedStaff)}
                className="px-4 py-2 rounded-xl bg-[#7cff01]/10 text-[#7cff01] text-xs font-bold transition flex items-center gap-2 border border-[#7cff01]/20"
              >
                <Edit size={14} /> Edit
              </button>
              <button
                onClick={() => handleDelete(selectedStaff.id)}
                disabled={processingId === selectedStaff.id}
                className="px-4 py-2 rounded-xl bg-red-500/10 text-red-400 text-xs font-bold transition flex items-center gap-2 border border-red-500/20"
              >
                {processingId === selectedStaff.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Remove
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            <div className="space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                <Mail size={14} /> Contact Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={14} className="text-gray-500 shrink-0" />
                  <span className="text-gray-300">{selectedStaff.email || '—'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone size={14} className="text-gray-500 shrink-0" />
                  <span className="text-gray-300">{selectedStaff.phone || '—'}</span>
                </div>
              </div>

              {(selectedStaff.emergency_contact_name || selectedStaff.emergency_contact_phone) && (
                <>
                  <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 flex items-center gap-2 pt-2">
                    <Shield size={14} /> Emergency Contact
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <User size={14} className="text-gray-500 shrink-0" />
                      <span className="text-gray-300">{selectedStaff.emergency_contact_name || '—'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Phone size={14} className="text-gray-500 shrink-0" />
                      <span className="text-gray-300">{selectedStaff.emergency_contact_phone || '—'}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                <Briefcase size={14} /> Employment Details
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={14} className="text-gray-500 shrink-0" />
                  <span className="text-gray-300">Start: {formatDate(selectedStaff.start_date)}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={14} className="text-gray-500 shrink-0" />
                  <span className="text-gray-300">End: {formatDate(selectedStaff.end_date)}</span>
                </div>
              </div>

              {selectedStaff.notes && (
                <>
                  <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 flex items-center gap-2 pt-2">
                    <FileText size={14} /> Notes
                  </h3>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{selectedStaff.notes}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ===== REPORT VIEW =====
  if (showReport) {
    const statusInfo = STATUS_CONFIG[showReport.employment_status]
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center gap-4 print:hidden">
          <button
            onClick={() => setShowReport(null)}
            className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            onClick={handlePrintReport}
            className="ml-auto px-4 py-2 rounded-xl bg-[#7cff01]/10 text-[#7cff01] text-xs font-bold transition flex items-center gap-2 border border-[#7cff01]/20"
          >
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>

        <div ref={reportRef} className="rounded-2xl border border-gray-800 bg-white text-black p-8 print:border-0 print:rounded-none">
          <div className="flex items-center justify-between border-b-2 border-black pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-black">{clubName}</h1>
              <p className="text-sm text-gray-600">Staff Report</p>
            </div>
            <div className="text-right text-sm text-gray-600">
              <p>Generated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

          <div className="flex items-start gap-6 mb-8">
            {showReport.photo_url ? (
              <img src={showReport.photo_url} alt={showReport.full_name} className="w-24 h-24 rounded-xl object-cover border-2 border-gray-300" />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                <User size={40} className="text-gray-400" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-black">{showReport.full_name}</h2>
              <p className="text-gray-600">{showReport.position}</p>
              <p className="text-sm mt-1">
                <span className="font-semibold">Status:</span> {statusInfo?.label}
                {showReport.contract_type && (
                  <> · <span className="font-semibold">Contract:</span> {CONTRACT_OPTIONS.find((c) => c.value === showReport.contract_type)?.label}</>
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider border-b border-gray-300 pb-1 mb-3">Contact Information</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold">Email:</span> {showReport.email || '—'}</p>
                <p><span className="font-semibold">Phone:</span> {showReport.phone || '—'}</p>
              </div>

              {(showReport.emergency_contact_name || showReport.emergency_contact_phone) && (
                <>
                  <h3 className="text-sm font-black uppercase tracking-wider border-b border-gray-300 pb-1 mb-3 mt-6">Emergency Contact</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-semibold">Name:</span> {showReport.emergency_contact_name || '—'}</p>
                    <p><span className="font-semibold">Phone:</span> {showReport.emergency_contact_phone || '—'}</p>
                  </div>
                </>
              )}
            </div>

            <div>
              <h3 className="text-sm font-black uppercase tracking-wider border-b border-gray-300 pb-1 mb-3">Employment Details</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold">Position:</span> {showReport.position}</p>
                <p><span className="font-semibold">Status:</span> {statusInfo?.label}</p>
                <p><span className="font-semibold">Contract:</span> {CONTRACT_OPTIONS.find((c) => c.value === showReport.contract_type)?.label || '—'}</p>
                <p><span className="font-semibold">Start Date:</span> {formatDate(showReport.start_date)}</p>
                <p><span className="font-semibold">End Date:</span> {formatDate(showReport.end_date)}</p>
              </div>

              {showReport.notes && (
                <>
                  <h3 className="text-sm font-black uppercase tracking-wider border-b border-gray-300 pb-1 mb-3 mt-6">Notes</h3>
                  <p className="text-sm whitespace-pre-wrap">{showReport.notes}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ===== ADD/EDIT FORM VIEW =====
  if (showAddForm) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center gap-4">
          <button
            onClick={closeForm}
            className="text-gray-500 hover:text-white font-bold text-sm tracking-widest uppercase transition flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Back to Staff
          </button>
        </div>

        <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6">
          <h2 className="text-xl font-display font-black text-white mb-6">
            {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
          </h2>

          {error && (
            <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/5 p-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-400 shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Position *
                </label>
                <select
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                  required
                >
                  <option value="">Select position</option>
                  {POSITION_OPTIONS.map((pos) => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                  placeholder="john@club.com"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                  placeholder="+234..."
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Employment Status
                </label>
                <select
                  value={formData.employment_status}
                  onChange={(e) => setFormData({ ...formData, employment_status: e.target.value as ClubStaff['employment_status'] })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                >
                  <option value="active">Active</option>
                  <option value="on_leave">On Leave</option>
                  <option value="contract">Contract</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Contract Type
                </label>
                <select
                  value={formData.contract_type}
                  onChange={(e) => setFormData({ ...formData, contract_type: e.target.value as '' | ClubStaff['contract_type'] })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                >
                  <option value="">Select contract type</option>
                  {CONTRACT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                  placeholder="Contact name"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
                  placeholder="+234..."
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                Photo
              </label>
              <ImageUpload
                onUpload={(url) => setFormData({ ...formData, photo_url: url })}
                currentUrl={formData.photo_url || null}
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50 resize-none"
                placeholder="Any additional notes..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="px-6 py-3 rounded-xl bg-[#7cff01] text-black text-sm font-black uppercase tracking-widest hover:bg-[#8fff33] transition"
              >
                {editingStaff ? 'Save Changes' : 'Add Staff Member'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-6 py-3 rounded-xl bg-gray-800 text-gray-300 text-sm font-bold hover:text-white transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ===== LIST VIEW (default) =====
  if (staffLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="text-[#7cff01] animate-spin" size={32} />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-3 flex items-center gap-2">
          <AlertCircle size={16} className="text-red-400 shrink-0" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users size={24} className="text-[#7cff01]" />
          <div>
            <h2 className="text-2xl font-display font-black text-white">Staff Management</h2>
            <p className="text-sm text-gray-500 mt-1">Manage your club's coaching and support staff</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setShowImport(!showImport); setImportResults(null) }}
            className="px-4 py-2.5 rounded-xl bg-gray-800 text-gray-300 hover:text-white text-xs font-bold transition flex items-center gap-2 border border-gray-700"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            onClick={handleExportCSV}
            disabled={filteredStaff.length === 0}
            className="px-4 py-2.5 rounded-xl bg-gray-800 text-gray-300 hover:text-white text-xs font-bold transition flex items-center gap-2 border border-gray-700 disabled:opacity-50"
          >
            <Download size={14} /> Export CSV
          </button>
          <button
            onClick={openAddForm}
            className="px-4 py-2.5 rounded-xl bg-[#7cff01] text-black text-xs font-black uppercase tracking-widest hover:bg-[#8fff33] transition flex items-center gap-2"
          >
            <Plus size={14} /> Add Staff
          </button>
        </div>
      </div>

      {/* Import Panel */}
      {showImport && (
        <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black uppercase tracking-widest text-white">Import Staff from CSV</h3>
            <button
              onClick={() => { setShowImport(false); setImportResults(null) }}
              className="p-1 rounded-lg text-gray-500 hover:text-white transition"
            >
              <X size={16} />
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Upload a CSV or Excel file with staff details. Required columns: <span className="text-gray-300">full_name, position</span>.{' '}
            <button onClick={downloadStaffTemplate} className="text-[#7cff01] hover:underline">
              Download template
            </button>
          </p>

          <div
            onClick={() => importFileRef.current?.click()}
            className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-[#7cff01]/30 transition cursor-pointer"
          >
            {importing ? (
              <Loader2 size={32} className="text-[#7cff01] animate-spin mx-auto" />
            ) : (
              <>
                <Upload size={32} className="text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Click to upload CSV or Excel file</p>
                <p className="text-xs text-gray-600 mt-1">.csv, .xls, .xlsx</p>
              </>
            )}
            <input
              ref={importFileRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleImportFile(e.target.files[0])
                e.target.value = ''
              }}
            />
          </div>

          {importResults && (
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4 space-y-2">
              <p className="text-sm font-bold text-white">
                Imported {importResults.success} staff member{importResults.success !== 1 ? 's' : ''}
              </p>
              {importResults.errors.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-bold text-red-400">{importResults.errors.length} error(s):</p>
                  {importResults.errors.slice(0, 10).map((err, i) => (
                    <p key={i} className="text-xs text-red-300">{err}</p>
                  ))}
                  {importResults.errors.length > 10 && (
                    <p className="text-xs text-gray-500">...and {importResults.errors.length - 10} more</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Analytics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-800 bg-[#0d1117] p-4">
          <p className="text-xs font-black uppercase tracking-widest text-gray-500">Total Staff</p>
          <p className="text-2xl font-display font-black text-white mt-1">{staff.length}</p>
        </div>
        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
          <p className="text-xs font-black uppercase tracking-widest text-green-500">Active</p>
          <p className="text-2xl font-display font-black text-green-400 mt-1">{statusCounts.active}</p>
        </div>
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4">
          <p className="text-xs font-black uppercase tracking-widest text-yellow-500">On Leave</p>
          <p className="text-2xl font-display font-black text-yellow-400 mt-1">{statusCounts.on_leave}</p>
        </div>
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <p className="text-xs font-black uppercase tracking-widest text-blue-500">Contract</p>
          <p className="text-2xl font-display font-black text-blue-400 mt-1">{statusCounts.contract}</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search staff..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-gray-900 border border-gray-800 text-white text-sm focus:outline-none focus:border-[#7cff01]/50"
          />
        </div>
        <div className="flex gap-1.5 bg-gray-950 p-1 rounded-xl border border-gray-800">
          {(['all', 'active', 'on_leave', 'contract', 'terminated'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition ${
                statusFilter === status
                  ? 'bg-[#7cff01]/10 text-[#7cff01] shadow-[inset_0_0_0_1px_rgba(124,255,1,0.2)]'
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              {status === 'all' ? 'All' : STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Staff List */}
      {filteredStaff.length === 0 ? (
        <div className="rounded-2xl border border-gray-800 bg-[#0d1117] p-12 text-center">
          <Users size={48} className="text-gray-700 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-400 mb-1">
            {staff.length === 0 ? 'No staff members yet' : 'No staff match your filters'}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {staff.length === 0
              ? 'Add your first staff member to get started'
              : 'Try adjusting your search or filters'}
          </p>
          {staff.length === 0 && (
            <button
              onClick={openAddForm}
              className="px-4 py-2 rounded-xl bg-[#7cff01]/10 text-[#7cff01] text-xs font-bold transition border border-[#7cff01]/20 inline-flex items-center gap-2"
            >
              <Plus size={14} /> Add Staff Member
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-800 bg-[#0d1117] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Staff Member</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500 hidden md:table-cell">Position</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500 hidden lg:table-cell">Contact</th>
                  <th className="text-right px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((member) => {
                  const statusInfo = STATUS_CONFIG[member.employment_status]
                  const StatusIcon = statusInfo?.icon || Activity
                  return (
                    <tr
                      key={member.id}
                      className="border-b border-gray-800/50 hover:bg-gray-800/20 cursor-pointer transition"
                      onClick={() => setSelectedStaff(member)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {member.photo_url ? (
                            <img src={member.photo_url} alt={member.full_name} className="w-9 h-9 rounded-lg object-cover border border-gray-700" />
                          ) : (
                            <div className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center border border-gray-700">
                              <User size={16} className="text-gray-500" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-bold text-white">{member.full_name}</p>
                            <p className="text-xs text-gray-500 md:hidden">{member.position}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-gray-300">{member.position}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${statusInfo?.bg} ${statusInfo?.color} ${statusInfo?.border}`}>
                          <StatusIcon size={10} />
                          {statusInfo?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <p className="text-xs text-gray-400">{member.email || '—'}</p>
                        <p className="text-xs text-gray-600">{member.phone || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedStaff(member)}
                            className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition"
                            title="View"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => openEditForm(member)}
                            className="p-2 rounded-lg text-gray-500 hover:text-[#7cff01] hover:bg-gray-800 transition"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(member.id)}
                            disabled={processingId === member.id}
                            className="p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-gray-800 transition disabled:opacity-50"
                            title="Delete"
                          >
                            {processingId === member.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClubStaffManagement
